import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import DashboardHeader from "../components/DashboardHeader";
import MinistryIcon from "../components/MinistryIcon";
import { useFeatureFlag } from "../lib/useFeatureFlag";

interface Ministry {
  name: string;
  slots: number;
}

interface Service {
  id: string;
  day: string;
  dayLabel: string;
  service: string;
  times: string;
  ministries: Ministry[];
}

// localStorage key — bump the .vN suffix when the shape changes so a
// stale payload from an older session falls back to defaults instead
// of crashing the renderer.
const STORAGE_KEY = "sacristy.holyWeek.v1";

// Ministries that auto-populate when Colleen taps "+ Add service".
// Reasonable starting ratio for OLG; she can tweak per service.
const DEFAULT_NEW_SERVICE_MINISTRIES: Ministry[] = [
  { name: "Sacristan", slots: 1 },
  { name: "EMHC", slots: 4 },
  { name: "Lector", slots: 2 },
  { name: "Usher", slots: 2 },
  { name: "Hospitality", slots: 1 },
];

// Quick-pick options on the "+ Add ministry" picker, plus an
// "Other…" escape hatch for free text.
const MINISTRY_OPTIONS = [
  "Sacristan",
  "EMHC",
  "Lector",
  "Usher",
  "Hospitality",
  "Altar Server",
  "Cantor",
  "Choir",
  "Musician",
];

// First-load defaults. Same visible layout the page has shown since
// PR #78. If localStorage is empty, the page renders exactly this.
const INITIAL_SERVICES: Service[] = [
  {
    id: "service-thursday",
    day: "Thursday, March 25",
    dayLabel: "Thursday, March 25",
    service: "Mass of the Lord's Supper",
    times: "7:00 PM",
    ministries: [
      { name: "Sacristan", slots: 1 },
      { name: "EMHC", slots: 8 },
      { name: "Lector", slots: 3 },
      { name: "Usher", slots: 4 },
      { name: "Hospitality", slots: 2 },
    ],
  },
  {
    id: "service-friday",
    day: "Friday, March 26",
    dayLabel: "Friday, March 26",
    service: "Celebration of the Lord's Passion",
    times: "12:00 PM and 7:00 PM",
    ministries: [
      { name: "Sacristan", slots: 1 },
      { name: "Lector", slots: 3 },
      { name: "Usher", slots: 4 },
    ],
  },
  {
    id: "service-saturday",
    day: "Saturday, March 27",
    dayLabel: "Saturday, March 27",
    service: "Easter Vigil",
    times: "8:30 PM",
    ministries: [
      { name: "Sacristan", slots: 2 },
      { name: "EMHC", slots: 10 },
      { name: "Lector", slots: 7 },
      { name: "Usher", slots: 4 },
      { name: "Hospitality", slots: 2 },
    ],
  },
];

function loadServices(): Service[] {
  if (typeof window === "undefined") return INITIAL_SERVICES;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (!stored) return INITIAL_SERVICES;
    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed) || parsed.length === 0) return INITIAL_SERVICES;
    return parsed as Service[];
  } catch {
    return INITIAL_SERVICES;
  }
}

function makeId(): string {
  return `s-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

export default function HolyWeek() {
  const enabled = useFeatureFlag("holyWeek");
  const [services, setServices] = useState<Service[]>(() => loadServices());

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(services));
    } catch {
      // quota / SSR — ignore, the next edit will retry.
    }
  }, [services]);

  if (!enabled) return <Navigate to="/" replace />;

  const patchService = (id: string, patch: Partial<Service>) => {
    setServices((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  };

  const removeService = (id: string) => {
    setServices((prev) => prev.filter((s) => s.id !== id));
  };

  const addService = () => {
    setServices((prev) => [
      ...prev,
      {
        id: makeId(),
        day: "New service",
        dayLabel: "New service",
        service: "Untitled",
        times: "",
        ministries: DEFAULT_NEW_SERVICE_MINISTRIES.map((m) => ({ ...m })),
      },
    ]);
  };

  return (
    <main className="page page--holy-week">
      <DashboardHeader />

      <header className="holy-week__header">
        <h1>Holy Week 2027</h1>
        <p className="holy-week__subtitle">March 25 – March 27</p>
      </header>

      <p className="holy-week__note">
        Holy Week planning happens here, separate from MSP. Plan the
        volunteers, send outreach, print the roster for your binder.
      </p>

      <section className="panel" aria-labelledby="services-title">
        <h2 id="services-title" className="panel__title">
          Services
        </h2>
        <ul className="holy-week__services">
          {services.map((s) => (
            <ServiceCard
              key={s.id}
              service={s}
              onPatch={(p) => patchService(s.id, p)}
              onRemove={() => removeService(s.id)}
            />
          ))}
        </ul>
        <button
          type="button"
          className="holy-week__add-service"
          onClick={addService}
        >
          + Add service
        </button>
      </section>
    </main>
  );
}

interface ServiceCardProps {
  service: Service;
  onPatch: (patch: Partial<Service>) => void;
  onRemove: () => void;
}

function ServiceCard({ service, onPatch, onRemove }: ServiceCardProps) {
  const [editing, setEditing] = useState(false);

  return (
    <li className="holy-week__service">
      <div className="holy-week__service-row">
        {editing ? (
          <div className="holy-week__service-edit">
            <input
              type="text"
              value={service.day}
              onChange={(e) =>
                onPatch({ day: e.target.value, dayLabel: e.target.value })
              }
              placeholder="Day (e.g. Thursday, March 25)"
              className="holy-week__edit-input holy-week__edit-input--date"
              autoFocus
            />
            <input
              type="text"
              value={service.service}
              onChange={(e) => onPatch({ service: e.target.value })}
              placeholder="Service title"
              className="holy-week__edit-input holy-week__edit-input--title"
            />
            <input
              type="text"
              value={service.times}
              onChange={(e) => onPatch({ times: e.target.value })}
              placeholder="Time (e.g. 7:00 PM)"
              className="holy-week__edit-input holy-week__edit-input--time"
            />
            <button
              type="button"
              className="holy-week__done-btn"
              onClick={() => setEditing(false)}
            >
              Done
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="holy-week__service-trigger"
            onClick={() => setEditing(true)}
            aria-label={`Edit ${service.service}`}
          >
            <div className="holy-week__service-date">
              {service.dayLabel || service.day}
            </div>
            <div className="holy-week__service-body">
              <span className="holy-week__service-name">{service.service}</span>
              <span className="holy-week__service-time">{service.times}</span>
            </div>
            <PencilIcon />
          </button>
        )}
        <button
          type="button"
          className="holy-week__service-delete"
          onClick={() => {
            const label = service.service || "this service";
            if (window.confirm(`Delete "${label}"?`)) onRemove();
          }}
          aria-label="Delete service"
          title="Delete service"
        >
          ×
        </button>
      </div>
      <MinistrySlots service={service} onPatch={onPatch} />
    </li>
  );
}

function MinistrySlots({
  service,
  onPatch,
}: {
  service: Service;
  onPatch: (patch: Partial<Service>) => void;
}) {
  const setMinistries = (next: Ministry[]) => onPatch({ ministries: next });

  const decrementSlot = (index: number) => {
    setMinistries(
      service.ministries
        .map((m, i) => (i === index ? { ...m, slots: m.slots - 1 } : m))
        .filter((m) => m.slots > 0),
    );
  };
  const incrementSlot = (index: number) => {
    setMinistries(
      service.ministries.map((m, i) =>
        i === index ? { ...m, slots: m.slots + 1 } : m,
      ),
    );
  };
  const removeMinistry = (index: number) => {
    setMinistries(service.ministries.filter((_, i) => i !== index));
  };
  const addMinistry = (name: string) => {
    setMinistries([...service.ministries, { name, slots: 1 }]);
  };

  return (
    <>
      <ul className="holy-week__slots">
        {service.ministries.map((m, i) => (
          <li key={i} className="holy-week__slot-row">
            <span className="holy-week__slot-label">
              <MinistryIcon ministry={m.name} />
              <span>{m.name}</span>
              <button
                type="button"
                className="holy-week__ministry-remove"
                onClick={() => removeMinistry(i)}
                aria-label={`Remove ${m.name} ministry`}
                title="Remove ministry"
              >
                ×
              </button>
            </span>
            <div className="holy-week__slot-pills">
              {Array.from({ length: m.slots }).map((_, j) => (
                <span key={j} className="holy-week__slot-pill">
                  <span className="holy-week__slot-pill-plus">+</span>
                  Unassigned
                  <button
                    type="button"
                    className="holy-week__slot-pill-remove"
                    onClick={() => decrementSlot(i)}
                    aria-label="Remove slot"
                    title="Remove slot"
                  >
                    ×
                  </button>
                </span>
              ))}
              <button
                type="button"
                className="holy-week__slot-add"
                onClick={() => incrementSlot(i)}
              >
                + Add slot
              </button>
            </div>
          </li>
        ))}
      </ul>
      <AddMinistryControl onAdd={addMinistry} />
    </>
  );
}

function AddMinistryControl({ onAdd }: { onAdd: (name: string) => void }) {
  const [mode, setMode] = useState<"closed" | "options" | "custom">("closed");
  const [custom, setCustom] = useState("");

  if (mode === "closed") {
    return (
      <button
        type="button"
        className="holy-week__add-ministry"
        onClick={() => setMode("options")}
      >
        + Add ministry
      </button>
    );
  }

  if (mode === "custom") {
    const commit = () => {
      const t = custom.trim();
      if (t) onAdd(t);
      setCustom("");
      setMode("closed");
    };
    return (
      <div className="holy-week__add-ministry-form">
        <input
          type="text"
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          placeholder="Ministry name"
          className="holy-week__edit-input"
          autoFocus
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            if (e.key === "Escape") {
              setCustom("");
              setMode("closed");
            }
          }}
        />
        <button type="button" className="holy-week__done-btn" onClick={commit}>
          Add
        </button>
        <button
          type="button"
          className="holy-week__cancel-btn"
          onClick={() => {
            setCustom("");
            setMode("closed");
          }}
        >
          Cancel
        </button>
      </div>
    );
  }

  return (
    <div className="holy-week__add-ministry-form">
      {MINISTRY_OPTIONS.map((name) => (
        <button
          key={name}
          type="button"
          className="holy-week__ministry-option"
          onClick={() => {
            onAdd(name);
            setMode("closed");
          }}
        >
          {name}
        </button>
      ))}
      <button
        type="button"
        className="holy-week__ministry-option holy-week__ministry-option--other"
        onClick={() => setMode("custom")}
      >
        Other…
      </button>
      <button
        type="button"
        className="holy-week__cancel-btn"
        onClick={() => setMode("closed")}
      >
        Cancel
      </button>
    </div>
  );
}

function PencilIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 16 16"
      aria-hidden="true"
      className="holy-week__pencil"
    >
      <path
        d="M2.5 13.5 L2.5 11 L11 2.5 L13.5 5 L5 13.5 Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <path d="M9.5 4 L12 6.5" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  );
}
